import * as Dialog from '@radix-ui/react-dialog'
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Globe2, Rocket, X } from 'lucide-react'

import {
  listProjectFastDeliveries,
  type FastDeliveryChangeSummary,
  type FastDeliveryTimelineEvent,
  type FastDelivery,
  type FastDeliveryProduction,
} from '../../../app/fast-deliveries'
import { publishMonthDayTime } from '../../../app/format'
import { useLiveActivity } from '../../../app/live-events'
import {
  useProject,
  useProjectFastDeliveries,
  useProjectFastDelivery,
  useStaticSiteDeployments,
  useStaticSites,
} from '../../../app/queries'
import { primaryStaticSite } from '../../../app/static-sites'
import { cn } from '../../../components/cn'
import { t } from '../../../copy'
import { pj } from '../../../copy/pjshell'
import { PJ } from '../../../copy/pjshell-text'
import type { PjshellId } from '../../../copy/pjshell'
import { StaticPublishWorkspace, deploymentStatusCopy } from '../static-sites'
import { useProjectId } from './params'
import {
  applicationSteps,
  currentMainShaFromDeliveries,
  deliveryReceipt,
  deliveryRollbackSha,
  failureActionCopy,
  failureFile,
  failureReason,
  failureStageCopy,
  fastDeliverySummary,
  fastDeliveryStatus,
  isBootstrapAdminRecovery,
  isFastDeliveryTerminal,
  mainMatchesProduction,
  showDeliveryFailure,
  visibleAffectedServices,
  deliveryLaneKind,
  isInfraFastReleaseId,
  isVerifiedProductionDelivery,
  evidenceTriad,
  type EvidenceTriadState,
} from './fast-delivery-projection'
import {
  GroupLabel,
  KvRows,
  ModuleHeader,
  ModuleNotice,
  ModulePage,
  ModulePanel,
  ModuleSkeleton,
  StatusPill,
  StepTimeline,
  Unverified,
  luluButtonClass,
  moduleTitle,
  type StepState,
} from './ui'

export { deploymentStatusCopy }
export { fastDeliveryStatus, type DeliveryStatus } from './fast-delivery-projection'

export function ProjectReleasesPage() {
  const projectId = useProjectId()
  if (!projectId) {
    return (
      <ModulePage testid="project-module-releases">
        <ModuleSkeleton />
      </ModulePage>
    )
  }
  return <Loaded projectId={projectId} />
}

export { ProjectReleasesPage as ProjectReleases }

function Loaded({ projectId }: { projectId: string }) {
  const project = useProject(projectId)
  const name = project.data?.name ?? t('nav.project.loading')
  const [tab, setTab] = useState<'application' | 'static'>('application')

  return (
    <ModulePage testid="project-module-releases">
      <ModuleHeader
        title={moduleTitle(name, t('nav.ptab.release'))}
        subtitle={<PJ id="pjshell.release.app.subtitle" />}
      />
      <div
        role="tablist"
        aria-label={t('nav.ptab.release')}
        className="mb-npc-grid flex min-h-11 w-fit max-w-full gap-2 overflow-x-auto"
      >
        <ProjectReleaseTab
          id="application"
          active={tab === 'application'}
          icon={<Rocket aria-hidden="true" size={16} />}
          onClick={() => setTab('application')}
        >
          <PJ id="pjshell.release.tab.application" />
        </ProjectReleaseTab>
        <ProjectReleaseTab
          id="static"
          active={tab === 'static'}
          icon={<Globe2 aria-hidden="true" size={16} />}
          onClick={() => setTab('static')}
        >
          <PJ id="pjshell.release.tab.static" />
        </ProjectReleaseTab>
      </div>
      <div role="tabpanel" aria-labelledby={`project-release-tab-${tab}`}>
        {tab === 'application' ? <ApplicationReleases projectId={projectId} /> : null}
        {tab === 'static' ? <StaticReleaseSection projectId={projectId} /> : null}
      </div>
    </ModulePage>
  )
}

function ProjectReleaseTab({
  id,
  active,
  icon,
  onClick,
  children,
}: {
  id: 'application' | 'static'
  active: boolean
  icon: ReactNode
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      id={`project-release-tab-${id}`}
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        'inline-flex h-[34px] shrink-0 items-center gap-npc-gap-xs rounded-full border px-3.5 text-npc-body-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--pj-accent)]',
        active
          ? 'pj-subtab-active'
          : 'border-npc-lulu-line-2 bg-transparent text-npc-lulu-t2 hover:text-npc-lulu-t1',
      )}
    >
      {icon}
      {children}
    </button>
  )
}

function ApplicationReleases({ projectId }: { projectId: string }) {
  const list = useProjectFastDeliveries(projectId)
  const live = useLiveActivity()
  const [older, setOlder] = useState<FastDelivery[]>([])
  const [olderCursor, setOlderCursor] = useState<string | null>(null)
  const [loadingOlder, setLoadingOlder] = useState(false)

  useEffect(() => {
    setOlder([])
    setOlderCursor(null)
  }, [projectId])

  const firstDeliveries = list.data?.deliveries ?? []
  const firstIds = new Set(firstDeliveries.map((item) => item.release_id))
  const deliveries = [...firstDeliveries, ...older.filter((item) => !firstIds.has(item.release_id))]
  const nextCursor = older.length === 0 ? list.data?.next_cursor?.trim() || null : olderCursor

  async function loadOlder() {
    const cursor = nextCursor
    if (!cursor || loadingOlder) return
    setLoadingOlder(true)
    try {
      const page = await listProjectFastDeliveries(projectId, cursor)
      setOlder((current) => [...current, ...page.deliveries])
      setOlderCursor(page.next_cursor?.trim() || null)
    } catch {
      // 保留当前游标，下一次再点同一页。
    } finally {
      setLoadingOlder(false)
    }
  }

  return (
    <section aria-labelledby="application-releases-title">
      <GroupLabel>
        <span id="application-releases-title">
          <PJ id="pjshell.release.app.title" />
        </span>
      </GroupLabel>

      {live.connectionStatus === 'disconnected' ? (
        <ModuleNotice
          tone="warn"
          title={<PJ id="pjshell.release.connection_lost" />}
          body={<PJ id="pjshell.release.connection_lost.body" />}
          testid="fast-delivery-stream-disconnected"
        />
      ) : null}

      {list.isPending ? (
        <div className="mb-npc-grid" data-testid="fast-deliveries-loading">
          <ModuleSkeleton />
        </div>
      ) : list.error || !list.data ? (
        <ModuleNotice
          tone="danger"
          title={<PJ id="pjshell.release.load_failed" />}
          body={<PJ id="pjshell.release.load_failed.body" />}
          testid="fast-deliveries-error"
        />
      ) : (
        <>
          <ReleaseBaseline
            deliveries={deliveries}
            production={list.data.current_production_baseline}
          />
          <DeliveryWorkspace
            projectId={projectId}
            deliveries={deliveries}
            hasOlder={Boolean(nextCursor)}
            loadingOlder={loadingOlder}
            onLoadOlder={() => void loadOlder()}
          />
        </>
      )}
    </section>
  )
}

function ReleaseBaseline({
  deliveries,
  production,
}: {
  deliveries: FastDelivery[]
  production: FastDeliveryProduction | null
}) {
  const mainSha = currentMainShaFromDeliveries(deliveries)
  const productionSha = production?.sha?.trim() ? production.sha.trim() : null
  const comparison = mainMatchesProduction(mainSha, productionSha)
  const border =
    comparison === 'match'
      ? 'border-npc-lulu-ok/35 bg-npc-lulu-ok/10'
      : comparison === 'mismatch'
        ? 'border-npc-lulu-bad/40 bg-npc-lulu-bad/10'
        : 'border-npc-lulu-line-2 bg-npc-lulu-bg-1'

  return (
    <section
      data-testid="fast-delivery-baseline"
      className={cn('pj-shell mb-7 border px-npc-grid-sm py-[13px]', border)}
    >
      <div
        data-testid="fast-delivery-current-main"
        className="grid grid-cols-1 gap-npc-gap-sm sm:grid-cols-3"
      >
        <ProductionFact label={<PJ id="pjshell.release.current_main" />}>
          {mainSha ? (
            <Commit value={mainSha} />
          ) : (
            <PJ id="pjshell.release.main_vs_production.unknown" />
          )}
        </ProductionFact>
        <ProductionFact label={<PJ id="pjshell.release.production" />}>
          {productionSha ? (
            <span className="inline-flex flex-wrap items-center gap-npc-gap-xs">
              <Commit value={productionSha} />
              <StatusPill tone="ok">
                <PJ id="pjshell.release.state.production_verified" />
              </StatusPill>
            </span>
          ) : (
            <PJ id="pjshell.release.main_vs_production.unknown" />
          )}
        </ProductionFact>
        <ProductionFact label={<PJ id="pjshell.release.main_vs_production" />}>
          {comparison === 'match' ? (
            <StatusPill tone="ok">
              <PJ id="pjshell.release.main_vs_production.match" />
            </StatusPill>
          ) : comparison === 'mismatch' ? (
            <StatusPill tone="bad">
              <PJ id="pjshell.release.main_vs_production.mismatch" />
            </StatusPill>
          ) : (
            <PJ id="pjshell.release.main_vs_production.unknown" />
          )}
        </ProductionFact>
      </div>

      {comparison === 'mismatch' ? (
        <div
          role="alert"
          data-testid="fast-delivery-mismatch-alert"
          className="mt-npc-gap-sm rounded-npc-chip border border-npc-lulu-bad/45 bg-npc-lulu-bad/15 px-npc-grid-sm py-[10px] text-npc-body-sm font-medium text-npc-lulu-bad"
        >
          <PJ id="pjshell.release.main_vs_production" />
          {' · '}
          <PJ id="pjshell.release.main_vs_production.mismatch" />
        </div>
      ) : null}

      {production ? (
        <div
          data-testid="fast-delivery-production"
          className="mt-npc-gap-sm grid grid-cols-1 gap-npc-gap-sm border-t border-npc-lulu-line-1 pt-npc-gap-sm sm:grid-cols-2 xl:grid-cols-4"
        >
          <ProductionFact label={<PJ id="pjshell.release.repository" />}>
            {production.repo}
          </ProductionFact>
          <ProductionFact label={<PJ id="pjshell.release.services" />}>
            {servicesText(production.services)}
          </ProductionFact>
          <ProductionFact label={<PJ id="pjshell.release.verified_at" />}>
            {publishMonthDayTime(production.verified_at)}
          </ProductionFact>
          <ProductionFact label={<PJ id="pjshell.release.previous" />}>
            <CommitOrUnknown value={production.previous_sha} />
          </ProductionFact>
        </div>
      ) : (
        <div
          data-testid="fast-delivery-production-empty"
          className="mt-npc-gap-sm border-t border-npc-lulu-line-1 pt-npc-gap-sm"
        >
          <div className="text-npc-body-sm font-medium text-npc-lulu-t1">
            <PJ id="pjshell.release.production.none" />
          </div>
          <div className="mt-npc-gap-xs text-npc-caption leading-[1.65] text-npc-lulu-t3">
            <PJ id="pjshell.release.production.none.body" />
          </div>
        </div>
      )}
    </section>
  )
}

function ProductionFact({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-npc-meta text-npc-lulu-t3">{label}</div>
      <div className="mt-0.5 break-words text-npc-caption text-npc-lulu-t1">{children}</div>
    </div>
  )
}

function EvidenceFact({ label, state }: { label: ReactNode; state: EvidenceTriadState }) {
  const copyId: PjshellId =
    state === 'confirmed'
      ? 'pjshell.release.evidence.yes'
      : state === 'unverified'
        ? 'pjshell.release.evidence.unverified'
        : 'pjshell.release.evidence.unknown'
  const tone =
    state === 'confirmed'
      ? 'text-npc-lulu-ok'
      : state === 'unverified'
        ? 'text-npc-lulu-warn'
        : 'text-npc-lulu-t3'
  return (
    <ProductionFact label={label}>
      <span className={tone} data-evidence-state={state}>
        <PJ id={copyId} />
      </span>
    </ProductionFact>
  )
}

function DeliveryWorkspace({
  projectId,
  deliveries,
  hasOlder,
  loadingOlder,
  onLoadOlder,
}: {
  projectId: string
  deliveries: FastDelivery[]
  hasOlder: boolean
  loadingOlder: boolean
  onLoadOlder: () => void
}) {
  const [requestedReleaseId, setRequestedReleaseId] = useState<string | null>(null)
  const [mobileDetailOpen, setMobileDetailOpen] = useState(false)
  const mobileTriggerRef = useRef<HTMLButtonElement | null>(null)
  const mobileCloseRef = useRef<HTMLButtonElement | null>(null)
  const selectedReleaseId = useMemo(() => {
    if (requestedReleaseId && deliveries.some((item) => item.release_id === requestedReleaseId)) {
      return requestedReleaseId
    }
    return deliveries[0]?.release_id ?? null
  }, [deliveries, requestedReleaseId])
  const selectedFromList = deliveries.find((item) => item.release_id === selectedReleaseId)
  const detail = useProjectFastDelivery(projectId, selectedReleaseId)
  const selected = detail.data?.delivery ?? selectedFromList

  if (deliveries.length === 0) {
    return (
      <div
        data-testid="fast-deliveries-empty"
        className="pj-card pj-empty-ish mb-7 border border-dashed border-npc-lulu-line-3 px-npc-grid-sm py-npc-gap-lg text-center"
      >
        <div className="text-npc-body-sm font-medium text-npc-lulu-t1">
          <PJ id="pjshell.release.empty" />
        </div>
        <div className="mt-npc-gap-xs text-npc-caption leading-[1.65] text-npc-lulu-t3">
          <PJ id="pjshell.release.empty.body" />
        </div>
      </div>
    )
  }

  return (
    <div className="mb-npc-grid grid min-w-0 grid-cols-1 gap-npc-grid min-[900px]:grid-cols-[minmax(0,1fr)_minmax(320px,0.78fr)]">
      <section aria-labelledby="recent-deliveries-title" className="min-w-0">
        <h2
          id="recent-deliveries-title"
          className="mb-npc-gap-sm mt-0 text-npc-body-sm font-medium text-npc-lulu-t1"
        >
          <PJ id="pjshell.release.recent" />
        </h2>
        <div className="pj-shell overflow-hidden">
          {deliveries.map((delivery, index) => {
            const status = fastDeliveryStatus(delivery)
            const summary = fastDeliverySummary(delivery)
            const selectedRow = delivery.release_id === selectedReleaseId
            return (
              <button
                key={delivery.release_id}
                type="button"
                aria-pressed={selectedRow}
                aria-label={`${pj('pjshell.release.detail.open')} ${shortCommit(delivery.candidate_sha)}`}
                data-testid={`fast-delivery-row-${delivery.release_id}`}
                className={cn(
                  'block w-full border-0 bg-transparent px-npc-grid-sm py-npc-gap-sm text-left transition-colors duration-npc-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-npc-lulu-t1',
                  index > 0 && 'border-t border-npc-lulu-line-1',
                  selectedRow ? 'bg-npc-lulu-bg-3' : 'hover:bg-npc-lulu-bg-2',
                )}
                onClick={(event) => {
                  setRequestedReleaseId(delivery.release_id)
                  if (window.innerWidth < 900) {
                    mobileTriggerRef.current = event.currentTarget
                    setMobileDetailOpen(true)
                  }
                }}
              >
                <div className="flex flex-wrap items-start justify-between gap-npc-gap-sm">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-npc-gap-xs">
                      <Commit value={delivery.candidate_sha} />
                      <StatusPill tone={status.tone}>
                        <PJ id={status.copyId} />
                      </StatusPill>
                      <DeliveryLaneBadges delivery={delivery} />
                    </div>
                    <div className="mt-npc-gap-xs truncate text-npc-caption text-npc-lulu-t2">
                      {shortIdentity(delivery.identity_id)} · {delivery.repo}
                    </div>
                  </div>
                  <time
                    className="shrink-0 text-npc-meta text-npc-lulu-t4"
                    dateTime={delivery.updated_at}
                  >
                    {publishMonthDayTime(delivery.updated_at)}
                  </time>
                </div>
                <div className="mt-npc-gap-xs line-clamp-2 text-npc-caption-sm leading-[1.55] text-npc-lulu-t3">
                  {summary.text || <PJ id={summary.copyId} />}
                </div>
                <div className="mt-npc-gap-xs flex min-w-0 flex-wrap items-center gap-npc-gap-sm text-npc-caption-sm text-npc-lulu-t3">
                  <Lineage delivery={delivery} />
                  <span className="min-w-0 break-all">
                    {servicesText(delivery.affected_services)}
                  </span>
                </div>
                <div className="mt-npc-gap-xs grid min-w-0 grid-cols-1 gap-npc-gap-xs text-npc-caption-sm text-npc-lulu-t4 sm:grid-cols-2">
                  <span className="min-w-0 truncate font-npc-mono" title={delivery.release_id}>
                    <PJ id="pjshell.release.id" /> {shortIdentity(delivery.release_id)}
                  </span>
                  {delivery.deployment_sha ? (
                    <span className="min-w-0">
                      <PJ id="pjshell.release.deployment" />{' '}
                      <Commit value={delivery.deployment_sha} />
                    </span>
                  ) : null}
                  {deliveryReceipt(delivery) ? (
                    <span
                      className="min-w-0 truncate font-npc-mono"
                      title={deliveryReceipt(delivery) ?? ''}
                    >
                      <PJ id="pjshell.release.receipt" />{' '}
                      {shortIdentity(deliveryReceipt(delivery) ?? '')}
                    </span>
                  ) : null}
                  {failureFile(delivery) ? (
                    <span className="min-w-0 break-all">{failureFile(delivery)}</span>
                  ) : null}
                </div>
              </button>
            )
          })}
        </div>
        {hasOlder ? (
          <button
            type="button"
            data-testid="fast-deliveries-load-older"
            className={cn(luluButtonClass, 'mt-npc-gap-sm min-h-11 w-full')}
            disabled={loadingOlder}
            onClick={onLoadOlder}
          >
            <PJ id={loadingOlder ? 'pjshell.release.older.loading' : 'pjshell.release.older'} />
          </button>
        ) : null}
      </section>

      <aside className="hidden min-w-0 min-[900px]:block" aria-label={pj('pjshell.release.detail')}>
        {!mobileDetailOpen && selected ? (
          <DeliveryDetail
            delivery={selected}
            timeline={detail.data?.timeline ?? []}
            changeSummary={detail.data?.change_summary ?? null}
            detailPending={detail.isPending}
            detailError={detail.isError}
          />
        ) : !mobileDetailOpen ? (
          <ModuleSkeleton />
        ) : null}
      </aside>

      <Dialog.Root open={mobileDetailOpen} onOpenChange={setMobileDetailOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-black/50" />
          <Dialog.Content
            aria-modal="true"
            aria-describedby={undefined}
            aria-labelledby="fast-delivery-mobile-title"
            data-testid="fast-delivery-mobile-dialog"
            className="fixed inset-0 z-50 overflow-y-auto bg-npc-lulu-bg-0 p-npc-grid-sm text-npc-lulu-t1"
            onOpenAutoFocus={(event) => {
              event.preventDefault()
              mobileCloseRef.current?.focus()
            }}
            onCloseAutoFocus={(event) => {
              event.preventDefault()
              mobileTriggerRef.current?.focus()
            }}
          >
            <div className="sticky top-0 z-10 mb-npc-gap-sm flex items-center justify-between border-b border-npc-lulu-line-1 bg-npc-lulu-bg-0 pb-npc-gap-sm">
              <Dialog.Title
                id="fast-delivery-mobile-title"
                className="m-0 text-npc-body-sm font-medium"
              >
                <PJ id="pjshell.release.detail" />
              </Dialog.Title>
              <Dialog.Close asChild>
                <button
                  ref={mobileCloseRef}
                  type="button"
                  className={cn(luluButtonClass, 'size-11 px-0')}
                  aria-label={pj('pjshell.release.detail.close')}
                >
                  <X aria-hidden="true" size={17} />
                </button>
              </Dialog.Close>
            </div>
            {selected ? (
              <DeliveryDetail
                delivery={selected}
                timeline={detail.data?.timeline ?? []}
                changeSummary={detail.data?.change_summary ?? null}
                detailPending={detail.isPending}
                detailError={detail.isError}
              />
            ) : (
              <ModuleSkeleton />
            )}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  )
}

function DeliveryDetail({
  delivery,
  timeline,
  changeSummary,
  detailPending,
  detailError,
}: {
  delivery: FastDelivery
  timeline: FastDeliveryTimelineEvent[]
  changeSummary: FastDeliveryChangeSummary | null
  detailPending: boolean
  detailError: boolean
}) {
  const status = fastDeliveryStatus(delivery)
  const summary = fastDeliverySummary(delivery)
  const triad = evidenceTriad(delivery)
  const showFailure = showDeliveryFailure(delivery)
  const now = useReleaseClock(!isFastDeliveryTerminal(delivery))
  const timedSteps = releaseTimedSteps(delivery, timeline, now)
  return (
    <ModulePanel
      title={<PJ id="pjshell.release.detail" />}
      badge={
        <>
          <StatusPill tone={status.tone}>
            <PJ id={status.copyId} />
          </StatusPill>
          <DeliveryLaneBadges delivery={delivery} />
        </>
      }
      testid="fast-delivery-detail"
    >
      {detailError ? (
        <div
          role="status"
          data-testid="fast-delivery-detail-stale"
          className="mb-npc-gap-sm border border-npc-lulu-warn/35 bg-npc-lulu-warn/10 px-npc-gap-sm py-npc-gap-sm"
        >
          <div className="text-npc-body-sm font-medium text-npc-lulu-warn">
            <PJ id="pjshell.release.detail.stale" />
          </div>
          <div className="mt-npc-gap-xs text-npc-caption-sm text-npc-lulu-t3">
            <PJ id="pjshell.release.detail.stale.body" />
          </div>
        </div>
      ) : detailPending ? (
        <div className="mb-npc-gap-sm">
          <ModuleSkeleton />
        </div>
      ) : null}
      <div className="text-npc-meta text-npc-lulu-t3">
        <PJ id="pjshell.release.summary" />
      </div>
      <p
        data-testid="fast-delivery-summary"
        className="mb-0 mt-npc-gap-xs text-npc-body-sm leading-[1.65] text-npc-lulu-t1"
      >
        {summary.text || <PJ id={summary.copyId} />}
      </p>

      <ReleaseChangeSummary summary={changeSummary} services={delivery.affected_services} />

      <div className="mt-npc-gap-sm flex min-w-0 flex-wrap items-center justify-between gap-npc-gap-sm border-t border-npc-lulu-line-1 pt-npc-gap-sm text-npc-caption-sm text-npc-lulu-t3">
        <Lineage delivery={delivery} />
        <span className="min-w-0 break-all">{servicesText(delivery.affected_services)}</span>
      </div>
      {delivery.coalesced_into ? (
        <div className="mt-npc-gap-xs flex min-w-0 flex-wrap gap-npc-gap-xs text-npc-caption-sm text-npc-lulu-t3">
          <PJ id="pjshell.release.coalesced_into" />
          <Commit value={delivery.coalesced_into} />
        </div>
      ) : null}

      <div className="mt-npc-gap-sm min-w-0 overflow-x-auto border-t border-npc-lulu-line-1 pt-npc-gap-sm">
        <KvRows
          wide
          rows={[
            {
              key: <PJ id="pjshell.release.id" />,
              value: <span className="break-all font-npc-mono">{delivery.release_id}</span>,
            },
            {
              key: <PJ id="pjshell.release.candidate" />,
              value: <Commit value={delivery.candidate_sha} />,
            },
            {
              key: <PJ id="pjshell.release.receipt" />,
              value: deliveryReceipt(delivery) ? (
                <span className="break-all font-npc-mono">{deliveryReceipt(delivery)}</span>
              ) : (
                <Unverified />
              ),
            },
            {
              key: <PJ id="pjshell.release.services" />,
              value: servicesText(delivery.affected_services),
            },
            {
              key: <PJ id="pjshell.release.deployment" />,
              value: <CommitOrUnknown value={delivery.deployment_sha} />,
            },
            {
              key: <PJ id="pjshell.release.rollback_point" />,
              value: <CommitOrUnknown value={deliveryRollbackSha(delivery)} />,
            },
          ]}
        />
      </div>

      {showFailure ? <FailureSummary delivery={delivery} /> : null}

      <div
        className="mt-npc-gap-sm border-t border-npc-lulu-line-1 pt-npc-gap-sm"
        data-testid="fast-delivery-evidence-triad"
      >
        <div className="mb-npc-gap-sm text-npc-body-sm font-medium text-npc-lulu-t1">
          <PJ id="pjshell.release.evidence" />
        </div>
        <div className="grid grid-cols-1 gap-npc-gap-sm sm:grid-cols-3 min-[900px]:grid-cols-1 xl:grid-cols-3">
          <EvidenceFact label={<PJ id="pjshell.release.receipt" />} state={triad.receipt} />
          <EvidenceFact label={<PJ id="pjshell.release.deployment" />} state={triad.deployment} />
          <EvidenceFact label={<PJ id="pjshell.release.evidence.online" />} state={triad.online} />
        </div>
      </div>

      <div className="mt-npc-gap-sm border-t border-npc-lulu-line-1 pt-npc-gap-sm">
        <div className="mb-npc-gap-sm flex items-baseline justify-between gap-npc-gap-sm">
          <span className="text-npc-body-sm font-medium text-npc-lulu-t1">
            <PJ id="pjshell.release.timing.total" />
          </span>
          <span className="font-npc-mono text-npc-caption text-npc-lulu-t2">
            {formatDuration(
              Math.max(0, terminalTime(delivery, now) - Date.parse(delivery.created_at)),
            )}
          </span>
        </div>
        <StepTimeline steps={timedSteps} testid="fast-delivery-timeline" />
      </div>

      <details className="mt-npc-gap-sm border-t border-npc-lulu-line-1 pt-npc-gap-sm">
        <summary className="min-h-10 cursor-pointer py-npc-gap-xs text-npc-body-sm font-medium text-npc-lulu-t2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-npc-lulu-t1">
          <PJ id="pjshell.release.technical" />
        </summary>
        <div className="pt-npc-gap-xs">
          <KvRows
            wide
            rows={[
              {
                key: <PJ id="pjshell.release.candidate" />,
                value: <Commit value={delivery.candidate_sha} />,
              },
              {
                key: <PJ id="pjshell.release.accepted_main" />,
                value: <CommitOrUnknown value={delivery.accepted_main_sha} />,
              },
              {
                key: <PJ id="pjshell.release.current_main" />,
                value: <CommitOrUnknown value={delivery.current_main_sha} />,
              },
              {
                key: <PJ id="pjshell.release.deployment" />,
                value: <CommitOrUnknown value={delivery.deployment_sha} />,
              },
              { key: <PJ id="pjshell.release.repository" />, value: delivery.repo },
              {
                key: <PJ id="pjshell.release.actor" />,
                value: <span className="font-npc-mono">{shortIdentity(delivery.identity_id)}</span>,
              },
              {
                key: <PJ id="pjshell.release.updated" />,
                value: publishMonthDayTime(delivery.updated_at),
              },
              {
                key: <PJ id="pjshell.release.verified_at" />,
                value: delivery.production_verified_at ? (
                  publishMonthDayTime(delivery.production_verified_at)
                ) : (
                  <Unverified />
                ),
              },
            ]}
          />
        </div>
      </details>
    </ModulePanel>
  )
}

function ReleaseChangeSummary({
  summary,
  services,
}: {
  summary: FastDeliveryChangeSummary | null
  services: string[]
}) {
  if (!summary) {
    return (
      <div className="mt-npc-gap-sm border-t border-npc-lulu-line-1 pt-npc-gap-sm text-npc-caption text-npc-lulu-t3">
        <PJ id="pjshell.release.change.empty" />
      </div>
    )
  }
  const impacts = Array.from(
    new Set([
      ...summary.areas.map(changeAreaLabel),
      ...visibleAffectedServices(services).map((item) =>
        pj('pjshell.release.change.affected_service', { name: item }),
      ),
    ]),
  )
  return (
    <section
      data-testid="fast-delivery-change-summary"
      className="mt-npc-gap-sm border-t border-npc-lulu-line-1 pt-npc-gap-sm"
    >
      <div className="text-npc-meta text-npc-lulu-t3">
        <PJ id="pjshell.release.change.title" />
      </div>
      <p className="mb-0 mt-npc-gap-xs text-npc-body-sm font-medium leading-[1.55] text-npc-lulu-t1">
        {summary.title}
      </p>
      <div className="mt-npc-gap-sm text-npc-meta text-npc-lulu-t3">
        <PJ id="pjshell.release.change.impact" />
      </div>
      <div className="mt-npc-gap-xs flex flex-wrap gap-npc-gap-xs">
        {impacts.map((item) => (
          <span
            key={item}
            className="rounded-npc-chip bg-npc-lulu-bg-3 px-2 py-1 text-npc-meta text-npc-lulu-t2"
          >
            {item}
          </span>
        ))}
      </div>
      <details className="mt-npc-gap-sm">
        <summary className="min-h-9 cursor-pointer text-npc-caption font-medium text-npc-lulu-t2">
          <PJ id="pjshell.release.change.files" /> ·{' '}
          {pj('pjshell.release.change.files_count', { count: String(summary.files_changed) })}
          <span className="ml-2 text-npc-lulu-ok">+{summary.additions}</span>
          <span className="ml-1 text-npc-lulu-bad">-{summary.deletions}</span>
        </summary>
        <ul className="m-0 mt-npc-gap-xs list-none space-y-1 p-0">
          {summary.files.map((file) => (
            <li
              key={file.path}
              className="grid grid-cols-[minmax(0,1fr)_auto] gap-npc-gap-sm text-npc-meta text-npc-lulu-t3"
            >
              <span className="min-w-0 break-all font-npc-mono">{file.path}</span>
              <span className="whitespace-nowrap font-npc-mono">
                {file.binary
                  ? pj('pjshell.release.change.binary')
                  : `+${file.additions} -${file.deletions}`}
              </span>
            </li>
          ))}
        </ul>
        {summary.truncated ? (
          <div className="mt-npc-gap-xs text-npc-meta text-npc-lulu-t4">
            <PJ id="pjshell.release.change.truncated" />
          </div>
        ) : null}
      </details>
    </section>
  )
}

const RELEASE_STEP_PHASES = [
  ['accepted', 'candidate_ready'],
  ['building', 'artifact_ready'],
  ['main_updated'],
  ['deploying'],
  ['deployed', 'production_verified', 'delivered_in_successor'],
] as const

const RELEASE_STEP_TIMEOUTS = [60_000, 10 * 60_000, 2 * 60_000, 10 * 60_000, 5 * 60_000] as const

function releaseTimedSteps(
  delivery: FastDelivery,
  timeline: FastDeliveryTimelineEvent[],
  now: number,
) {
  const base = applicationSteps(delivery)
  const timestamps = RELEASE_STEP_PHASES.map((phases, index) => {
    const values = timeline
      .filter((event) => (phases as readonly string[]).includes(event.phase))
      .map((event) => Date.parse(event.occurred_at))
      .filter(Number.isFinite)
    if (index === 0) values.push(Date.parse(delivery.created_at))
    return values.length > 0 ? Math.min(...values) : null
  })
  return base.map((step, index) => {
    const started = timestamps[index] ?? null
    if (started === null || step.state === 'wait') return step
    const next = timestamps.slice(index + 1).find((value) => value !== null) ?? null
    const ended =
      step.state === 'done'
        ? (next ?? terminalTime(delivery, now))
        : step.state === 'fail'
          ? Date.parse(delivery.updated_at)
          : now
    const elapsed = Math.max(0, ended - started)
    const expected = RELEASE_STEP_TIMEOUTS[index] ?? 0
    let meta: ReactNode
    if (step.state === 'now' && elapsed > expected) {
      meta = (
        <>
          {formatDuration(elapsed)} ·{' '}
          <PJ
            id="pjshell.release.timing.overdue"
            vars={{ duration: formatDuration(elapsed - expected) }}
          />
        </>
      )
    } else if (step.state === 'now') {
      meta = (
        <>
          {formatDuration(elapsed)} ·{' '}
          <PJ id="pjshell.release.timing.expected" vars={{ duration: formatDuration(expected) }} />
        </>
      )
    } else {
      meta = (
        <>
          {publishClock(ended)} · {formatDuration(elapsed)}
        </>
      )
    }
    return { ...step, meta }
  })
}

function useReleaseClock(active: boolean): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!active) return
    const timer = window.setInterval(() => setNow(Date.now()), 1_000)
    return () => window.clearInterval(timer)
  }, [active])
  return now
}

function terminalTime(delivery: FastDelivery, now: number): number {
  if (!isFastDeliveryTerminal(delivery)) return now
  const parsed = Date.parse(delivery.production_verified_at || delivery.updated_at)
  return Number.isFinite(parsed) ? parsed : now
}

function formatDuration(milliseconds: number): string {
  const seconds = Math.max(0, Math.floor(milliseconds / 1_000))
  if (seconds < 60) return pj('pjshell.release.timing.seconds', { seconds: String(seconds) })
  const minutes = Math.floor(seconds / 60)
  const rest = seconds % 60
  if (minutes < 60) {
    return rest > 0
      ? pj('pjshell.release.timing.minutes_seconds', {
          minutes: String(minutes),
          seconds: String(rest),
        })
      : pj('pjshell.release.timing.minutes', { minutes: String(minutes) })
  }
  const hours = Math.floor(minutes / 60)
  const minuteRest = minutes % 60
  return minuteRest > 0
    ? pj('pjshell.release.timing.hours_minutes', {
        hours: String(hours),
        minutes: String(minuteRest),
      })
    : pj('pjshell.release.timing.hours', { hours: String(hours) })
}

function publishClock(value: number): string {
  return new Intl.DateTimeFormat('zh-CN', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(value)
}

function changeAreaLabel(area: string): string {
  if (area === 'console') return pj('pjshell.release.change.area.console')
  if (area === 'database') return pj('pjshell.release.change.area.database')
  if (area === 'api_contract') return pj('pjshell.release.change.area.api_contract')
  if (area === 'shared_packages') return pj('pjshell.release.change.area.shared_packages')
  if (area === 'deployment') return pj('pjshell.release.change.area.deployment')
  if (area === 'documentation') return pj('pjshell.release.change.area.documentation')
  if (area.startsWith('service:')) {
    return pj('pjshell.release.change.area.service', { name: area.slice(8) })
  }
  return area
}

function FailureSummary({ delivery }: { delivery: FastDelivery }) {
  const oldVersionCopy =
    delivery.old_version_healthy === true
      ? 'pjshell.release.failure.old_healthy'
      : delivery.old_version_healthy === false
        ? 'pjshell.release.failure.old_unhealthy'
        : 'pjshell.release.failure.old_unknown'
  const oldVersionTone =
    delivery.old_version_healthy === true
      ? 'text-npc-lulu-ok'
      : delivery.old_version_healthy === false
        ? 'text-npc-lulu-bad'
        : 'text-npc-lulu-warn'
  const file = failureFile(delivery)
  const reason = failureReason(delivery)
  const stage = failureStageCopy(delivery)
  const bootstrap = isBootstrapAdminRecovery(delivery)
  return (
    <div
      data-testid="fast-delivery-failure"
      className="mt-npc-gap-sm bg-npc-lulu-bad/10 px-npc-gap-sm py-npc-gap-sm"
    >
      {bootstrap ? (
        <div
          data-testid="fast-delivery-failure-bootstrap"
          className="mb-npc-gap-sm text-npc-body-sm font-medium text-npc-lulu-warn"
        >
          <PJ id="pjshell.release.failure.bootstrap_label" />
        </div>
      ) : null}
      <KvRows
        wide
        rows={[
          {
            key: <PJ id="pjshell.release.failure.code" />,
            value: isBootstrapAdminRecovery(delivery) ? (
              <PJ id="pjshell.release.failure.bootstrap_label" />
            ) : (
              delivery.failure_code || <PJ id="pjshell.release.main_vs_production.unknown" />
            ),
            testid: 'fast-delivery-failure-code',
          },
          {
            key: <PJ id="pjshell.release.failure.file" />,
            value: file || <PJ id="pjshell.release.main_vs_production.unknown" />,
            testid: 'fast-delivery-failure-file',
          },
          {
            key: <PJ id="pjshell.release.failure.stage" />,
            value: stage ? (
              <PJ id={stage} />
            ) : (
              <PJ id="pjshell.release.main_vs_production.unknown" />
            ),
            testid: 'fast-delivery-failure-stage',
          },
          {
            key: <PJ id="pjshell.release.failure.reason" />,
            value: reason || <PJ id="pjshell.release.main_vs_production.unknown" />,
            testid: 'fast-delivery-failure-reason',
          },
          {
            key: <PJ id="pjshell.release.failure.action" />,
            value: <PJ id={failureActionCopy(delivery)} />,
            testid: 'fast-delivery-failure-action',
          },
        ]}
      />
      <div
        data-testid="fast-delivery-failure-old-version"
        className={cn('mt-npc-gap-sm text-npc-body-sm font-medium', oldVersionTone)}
      >
        <PJ id={oldVersionCopy} />
      </div>
    </div>
  )
}

function Lineage({ delivery }: { delivery: FastDelivery }) {
  if (delivery.lineage_relation === 'unknown') return <Unverified />
  if (
    delivery.lineage_relation === 'missing' &&
    (delivery.phase === 'accepted' ||
      delivery.phase === 'building' ||
      delivery.phase === 'artifact_ready' ||
      delivery.phase === 'candidate_ready')
  ) {
    return <Unverified />
  }
  const copyId =
    delivery.lineage_relation === 'included_in_newer'
      ? 'pjshell.release.state.included'
      : delivery.lineage_relation === 'exact'
        ? 'pjshell.release.state.exact'
        : 'pjshell.release.state.missing'
  const tone = delivery.lineage_relation === 'missing' ? 'text-npc-lulu-bad' : 'text-npc-lulu-ok'
  return (
    <span className={cn('font-medium', tone)}>
      <PJ id={copyId} />
    </span>
  )
}

function Commit({ value }: { value: string }) {
  return (
    <span className="font-npc-mono text-npc-caption text-npc-lulu-t1" title={value}>
      {shortCommit(value)}
    </span>
  )
}

function CommitOrUnknown({ value }: { value?: string | null }) {
  return value ? <Commit value={value} /> : <Unverified />
}

function shortCommit(value: string): string {
  return value.slice(0, 8)
}

function shortIdentity(value: string): string {
  return value.slice(0, 8)
}

function servicesText(services: string[]): ReactNode {
  const visible = visibleAffectedServices(services)
  return visible.length > 0 ? visible.join('、') : <PJ id="pjshell.release.services.none" />
}

function StaticReleaseSection({ projectId }: { projectId: string }) {
  const sites = useStaticSites(projectId)
  const primary = primaryStaticSite(sites.data)
  const deployments = useStaticSiteDeployments(projectId, primary?.id ?? null)
  const latest = deployments.data?.[0]
  const status = latest ? deploymentStatusCopy(latest) : 'pjshell.release.none'

  return (
    <section aria-labelledby="static-release-title">
      <GroupLabel>
        <span id="static-release-title">
          <PJ id="pjshell.release.static.title" />
        </span>
      </GroupLabel>
      <p className="mb-npc-gap-sm mt-0 text-npc-caption text-npc-lulu-t3">
        <PJ id="pjshell.release.static.body" />
      </p>
      <StaticPublishWorkspace
        scope={{ kind: 'project', projectId }}
        surface="lulu"
        showTitle={false}
        aside={
          <ModulePanel title={<PJ id="pjshell.release.steps" />} testid="pjshell-release-steps">
            <StaticReleaseSteps
              status={status}
              pending={sites.isPending || (primary != null && deployments.isPending)}
            />
          </ModulePanel>
        }
      />
    </section>
  )
}

function StaticReleaseSteps({ status, pending }: { status: PjshellId; pending: boolean }) {
  if (pending) return <ModuleSkeleton />
  return <StepTimeline steps={staticStepsFor(status)} testid="pjshell-release-timeline" />
}

function staticStepsFor(
  status: PjshellId,
): Array<{ title: PjshellId; state: StepState; note?: PjshellId }> {
  const check: StepState =
    status === 'pjshell.release.none'
      ? 'wait'
      : status === 'pjshell.release.checking'
        ? 'now'
        : 'done'
  const publish: StepState =
    status === 'pjshell.release.publishing'
      ? 'now'
      : status === 'pjshell.release.verifying' || status === 'pjshell.release.live'
        ? 'done'
        : status === 'pjshell.release.failed'
          ? 'fail'
          : 'wait'
  const verify: StepState =
    status === 'pjshell.release.verifying'
      ? 'now'
      : status === 'pjshell.release.live'
        ? 'done'
        : 'wait'
  const live: StepState = status === 'pjshell.release.live' ? 'done' : 'wait'
  return [
    { title: 'pjshell.release.step.check', state: check },
    { title: 'pjshell.release.step.publish', state: publish },
    {
      title: 'pjshell.release.step.verify',
      state: verify,
      note: 'pjshell.release.step.verify.note',
    },
    { title: 'pjshell.release.live', state: live, note: 'pjshell.release.step.live.note' },
  ]
}

function DeliveryLaneBadges({ delivery }: { delivery: FastDelivery }) {
  if (isVerifiedProductionDelivery(delivery)) {
    return (
      <StatusPill tone="ok">
        <PJ id="pjshell.release.badge.verified_production" />
      </StatusPill>
    )
  }
  if (delivery.phase === 'delivered_in_successor') return null
  const synthetic =
    isInfraFastReleaseId(delivery.deployment_release_id) ||
    isInfraFastReleaseId(delivery.release_id)
  const lane = deliveryLaneKind(delivery)
  if (lane !== 'fast_candidate' && !synthetic) return null
  return (
    <>
      <StatusPill tone="neutral">
        <PJ id="pjshell.release.badge.fast_candidate" />
      </StatusPill>
      {synthetic ? (
        <StatusPill tone="neutral">
          <PJ id="pjshell.release.badge.infra_fast" />
        </StatusPill>
      ) : null}
    </>
  )
}
